import { useMemo, useState } from "react";

const sanitizeHtml = (rawHtml) =>
  String(rawHtml || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

function PostPage({
  activePost,
  blogsLoading,
  isLoggedIn,
  handleEdit,
  handleDelete,
  sequenceSuggestions,
  openPost,
  goHome,
  onReact,
}) {
  const [isReacting, setIsReacting] = useState(false);
  const [reactionError, setReactionError] = useState("");
  const [likeBurstKey, setLikeBurstKey] = useState(0);

  const renderedContent = useMemo(() => {
    if (!activePost) return "";
    const safeTitle = String(activePost.title || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const safeContent = sanitizeHtml(activePost.content || "");
    const withInlineImages = safeContent.replace(/\[\[img:([^\]]+)\]\]/g, (_match, srcValue) => {
      const src = String(srcValue || "").trim();
      if (!src) return "";
      return `<img class="post-inline-image" src="${src}" alt="${safeTitle} inline visual" loading="lazy" />`;
    });
    return withInlineImages.replace(/\n/g, "<br />");
  }, [activePost]);

  if (!activePost && !blogsLoading) {
    return (
      <main className="main">
        <section className="admin-card">
          <h2>Post Not Found</h2>
          <p>
            This blog link is unavailable right now. It may have been deleted or
            never saved in this browser.
          </p>
          <button className="btn-secondary" type="button" onClick={goHome}>
            Go back to Blogs
          </button>
        </section>
      </main>
    );
  }

  if (!activePost) return null;

  const handleReactionClick = async (reactionType) => {
    if (!activePost?.id || !onReact || isReacting) return;
    setReactionError("");
    try {
      setIsReacting(true);
      await onReact(activePost.id, reactionType);
      if (reactionType === "like") {
        setLikeBurstKey((current) => current + 1);
      }
    } catch (error) {
      setReactionError(error?.message || "Could not submit reaction right now.");
    } finally {
      setIsReacting(false);
    }
  };

  return (
    <main className="main">
      <section className="post-page">
        <button className="btn-secondary back-btn" type="button" onClick={goHome}>
          Back to Blogs
        </button>
        <div className="post-layout">
          <article className="post-article">
            <div className="post-illustration" aria-hidden="true">
              <svg viewBox="0 0 120 54" role="img">
                <rect x="2" y="7" width="116" height="40" rx="12" fill="#e9f5ff" />
                <rect x="16" y="19" width="44" height="8" rx="4" fill="#2c82bf" />
                <rect x="67" y="19" width="38" height="8" rx="4" fill="#58a9da" />
                <circle cx="30" cy="36" r="5" fill="#2c82bf" opacity="0.75" />
                <circle cx="45" cy="36" r="5" fill="#2c82bf" opacity="0.52" />
              </svg>
            </div>
            <p className="blog-date">
              {activePost.updatedAt
                ? `Updated ${new Date(activePost.updatedAt).toLocaleDateString()}`
                : `Published ${new Date(activePost.createdAt).toLocaleDateString()}`}
            </p>
            <h1>{activePost.title}</h1>
            {activePost.bannerImage && (
              <img
                className="post-banner"
                src={activePost.bannerImage}
                alt={`${activePost.title} banner`}
                loading="lazy"
                style={{
                  objectPosition: `${activePost.bannerFocusX ?? 50}% ${activePost.bannerFocusY ?? 50}%`,
                }}
              />
            )}
            <div
              className="post-rich-content"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
            {Array.isArray(activePost.images) && activePost.images.length > 0 && (
              <div className="post-gallery">
                {activePost.images.map((image, index) => (
                  <img
                    key={`${image}-${index}`}
                    src={image}
                    alt={`${activePost.title} visual ${index + 1}`}
                    loading="lazy"
                    style={{
                      objectPosition: `${activePost.imageFocusX ?? 50}% ${activePost.imageFocusY ?? 50}%`,
                    }}
                  />
                ))}
              </div>
            )}
            {!!activePost.links && (
              <div className="link-row">
                {activePost.links.split("|").map((item) => {
                  const href = item.trim();
                  if (!href) return null;
                  return (
                    <a key={href} href={href} target="_blank" rel="noreferrer">
                      {href}
                    </a>
                  );
                })}
              </div>
            )}
            <div className="post-reaction-wrap">
              <div className="post-reaction-head">
                <span className="post-reaction-note">Dislikes are private</span>
              </div>
              <div className="post-reaction-actions">
                <button
                  className={`reaction-btn like ${isReacting ? "is-waiting" : ""} ${
                    activePost.userReaction === "like" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => handleReactionClick("like")}
                  disabled={isReacting}
                  aria-label="Like this blog"
                >
                  <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                    <path
                      d="M10.5 22H6a2 2 0 0 1-2-2v-7.8a2 2 0 0 1 2-2h4.5V22Zm2-10.7 2.8-6.3c.3-.8 1.2-1.2 2-.9c.7.2 1.1 1 .9 1.8l-1 4h3.9c1.2 0 2 .9 1.8 2l-1.3 7.3a2 2 0 0 1-2 1.8h-7.1V11.3Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="reaction-count" key={likeBurstKey}>
                    {activePost.likes || 0}
                  </span>
                </button>
                <button
                  className={`reaction-btn dislike ${
                    activePost.userReaction === "dislike" ? "is-active" : ""
                  }`}
                  type="button"
                  onClick={() => handleReactionClick("dislike")}
                  disabled={isReacting}
                  aria-label="Dislike this blog"
                >
                  <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                    <path
                      d="M13.5 2H18a2 2 0 0 1 2 2v7.8a2 2 0 0 1-2 2h-4.5V2Zm-2 10.7-2.8 6.3c-.3.8-1.2 1.2-2 .9c-.7-.2-1.1-1-.9-1.8l1-4H2.9c-1.2 0-2-.9-1.8-2L2.4 4.8a2 2 0 0 1 2-1.8h7.1v9.7Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
              {reactionError && <p className="error">{reactionError}</p>}
            </div>
            {isLoggedIn && (
              <div className="btn-row">
                <button className="btn-secondary" type="button" onClick={() => handleEdit(activePost)}>
                  Edit
                </button>
                <button className="btn-danger" type="button" onClick={() => handleDelete(activePost.id)}>
                  Delete
                </button>
              </div>
            )}
          </article>

          {sequenceSuggestions.length > 0 && (
            <aside className="post-suggest">
              <h3>More In Sequence</h3>
              {sequenceSuggestions.map((blog, index) => (
                <button
                  key={blog.id}
                  className="suggest-item"
                  type="button"
                  onClick={() => openPost(blog.id)}
                >
                  <span>{`0${index + 1}`.slice(-2)}</span>
                  <p>{blog.title}</p>
                </button>
              ))}
            </aside>
          )}
        </div>
      </section>
    </main>
  );
}

export default PostPage;
